
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type Photo } from '../src/api/albums';

interface LifeAtUHomesProps {
  onSearchJobs?: () => void;
  onOffices?: () => void;
}

/**
 * 精选照片区组件 - Airbnb 拼接风格
 * 取第一个已发布相册的照片展示
 */
const FeaturedPhotos: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    // 取照片最多的已发布相册
    import('../src/api/albums').then(({ getAlbums }) => {
      getAlbums().then(albums => {
        // 找照片最多的相册
        let best = albums[0];
        for (const a of albums) {
          if ((a.photos?.length || 0) > (best?.photos?.length || 0)) {
            best = a;
          }
        }
        if (best && best.photos && best.photos.length > 0) {
          setPhotos(best.photos);
        }
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') setLightboxIndex(i => i !== null ? (i > 0 ? i - 1 : photos.length - 1) : null);
      if (e.key === 'ArrowRight') setLightboxIndex(i => i !== null ? (i < photos.length - 1 ? i + 1 : 0) : null);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, photos.length]);

  if (photos.length < 1) return null;

  const getImageUrl = (path: string) => {
    if (path.startsWith('http')) return path;
    return path;
  };

  const mainPhoto = photos[0];
  const sidePhotos = photos.slice(1, 5);
  const bottomPhotos = photos.slice(5, 8);
  const useSimpleGrid = photos.length < 3;

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="text-sm font-bold text-blue-600 uppercase tracking-[0.2em] mb-3">{t('gallery.title')}</p>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">{t('gallery.subtitle')}</h2>
        </div>

        {/* 照片布局 - 错落排列风格 */}
        {useSimpleGrid ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {photos.map((photo, idx) => (
              <div
                key={photo.id}
                className="relative cursor-pointer group"
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={getImageUrl(photo.thumbUrl || photo.url)}
                  alt={isZh ? (photo.title || '') : (photo.titleEn || photo.title || '')}
                  className="w-full aspect-[3/2] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* 第一行：2张大图错落 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6 items-center">
              <div
                className="md:col-span-5 md:col-start-2 cursor-pointer group"
                onClick={() => setLightboxIndex(0)}
              >
                <img
                  src={getImageUrl(photos[0].url)}
                  alt=""
                  className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                />
              </div>
              <div
                className="md:col-span-5 md:mt-12 cursor-pointer group"
                onClick={() => setLightboxIndex(1)}
              >
                <img
                  src={getImageUrl(photos[1]?.url || photos[0].url)}
                  alt=""
                  className="w-full aspect-[3/2] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                />
              </div>
            </div>

            {/* 第二行：3张中图 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {photos.slice(2, 5).map((photo, idx) => (
                <div
                  key={photo.id}
                  className={`cursor-pointer group ${idx === 1 ? 'md:-mt-8' : idx === 2 ? 'md:mt-4' : ''}`}
                  onClick={() => setLightboxIndex(idx + 2)}
                >
                  <img
                    src={getImageUrl(photo.thumbUrl || photo.url)}
                    alt=""
                    className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* 第三行：2张大图错落（反向） */}
            {photos.length > 5 && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                <div
                  className="md:col-span-5 md:col-start-1 md:mt-8 cursor-pointer group"
                  onClick={() => setLightboxIndex(5)}
                >
                  <img
                    src={getImageUrl(photos[5]?.url || '')}
                    alt=""
                    className="w-full aspect-[3/2] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                    loading="lazy"
                  />
                </div>
                {photos[6] && (
                  <div
                    className="md:col-span-5 md:col-start-7 cursor-pointer group"
                    onClick={() => setLightboxIndex(6)}
                  >
                    <img
                      src={getImageUrl(photos[6].url)}
                      alt=""
                      className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg group-hover:shadow-2xl group-hover:scale-[1.02] transition-all duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            style={{ position: 'absolute', top: 20, right: 20, color: 'rgba(255,255,255,0.8)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i !== null ? (i > 0 ? i - 1 : photos.length - 1) : 0); }}
              style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <img
            src={getImageUrl(photos[lightboxIndex].url)}
            alt=""
            style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
          {photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i !== null ? (i < photos.length - 1 ? i + 1 : 0) : 0); }}
              style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            {lightboxIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </section>
  );
};

const LifeAtUHomes: React.FC<LifeAtUHomesProps> = ({ onSearchJobs, onOffices }) => {
  const { t } = useTranslation();

  // 平滑滚动到内容区域
  const scrollToContent = () => {
    const visionSection = document.getElementById('vision-section');
    if (visionSection) {
      visionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-white">
      {/* Hero - Intro Hook */}
      <section className="relative h-auto sm:h-[80vh] min-h-0 sm:min-h-[600px] pt-24 pb-12 sm:pt-16 sm:pb-0 flex items-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=2000"
          className="absolute inset-0 w-full h-full object-cover"
          alt={t('lifeAtUHomes.teamImageAlt')}
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-blue-900/50 to-blue-800/40"></div>

        {/* Decorative Gradient Orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-white/90 text-sm font-medium">{t('lifeAtUHomes.heroBadge') || '正在招聘中'}</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 tracking-tight leading-[1.1]">
              {t('lifeAtUHomes.heroTitle')} <span className="text-blue-400">{t('lifeAtUHomes.heroHighlight')}</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto font-medium leading-relaxed mb-4">
              {t('lifeAtUHomes.heroSubtitle')}
            </p>
            {t('lifeAtUHomes.slogan') && (
              <p className="text-lg md:text-xl text-blue-300 font-semibold mb-10">
                {t('lifeAtUHomes.slogan')}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onSearchJobs}
                className="bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:bg-slate-50 active:scale-95 transition-all cursor-pointer"
              >
                {t('lifeAtUHomes.heroCtaPrimary') || '查看开放职位'}
              </button>
              <button
                onClick={scrollToContent}
                className="bg-white/10 text-white border-2 border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-white/50 transition-all cursor-pointer"
              >
                {t('lifeAtUHomes.heroCtaSecondary') || '了解我们的文化'}
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="flex flex-wrap justify-center gap-6 sm:gap-8 md:gap-16 mt-8 sm:mt-16 pt-8 sm:pt-12 border-t border-white/10">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-1 drop-shadow-lg">{t('lifeAtUHomes.statTeamMembers') || '1200+'}</div>
              <div className="text-slate-200 text-xs sm:text-sm font-medium uppercase tracking-wider drop-shadow">{t('stats.teamMembers') || '团队成员'}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-1 drop-shadow-lg">{t('lifeAtUHomes.statOffices') || '33'}</div>
              <div className="text-slate-200 text-xs sm:text-sm font-medium uppercase tracking-wider drop-shadow">{t('stats.globalOffices') || '全球办公室'}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-1 drop-shadow-lg">{t('lifeAtUHomes.statStudents') || '3M+'}</div>
              <div className="text-slate-200 text-xs sm:text-sm font-medium uppercase tracking-wider drop-shadow">{t('stats.studentsServed') || '服务学生'}</div>
            </div>
          </div>
        </div>


      </section>

      {/* Vision - Chapter 1 */}
      <section id="vision-section" className="py-24 bg-gradient-to-br from-blue-50 to-slate-50 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm font-black text-blue-600 uppercase tracking-[0.3em] mb-4">{t('lifeAtUHomes.visionTitle')}</p>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            {t('lifeAtUHomes.visionText')}
          </h2>
        </div>
      </section>

      {/* Mission - Chapter 2 with Images */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-sm font-black text-blue-600 uppercase tracking-[0.3em] mb-4">{t('lifeAtUHomes.missionTitle')}</p>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 tracking-tight leading-tight">
              {t('lifeAtUHomes.missionText')}
            </h3>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              {t('lifeAtUHomes.missionDesc')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-56 lg:h-64 rounded-2xl overflow-hidden shadow-xl">
              <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=600" className="w-full h-full object-cover" alt={t('lifeAtUHomes.officeImageAlt')} loading="lazy" />
            </div>
            <div className="h-56 lg:h-64 rounded-2xl overflow-hidden shadow-xl lg:mt-12">
              <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=600" className="w-full h-full object-cover" alt={t('lifeAtUHomes.teamWorkImageAlt')} loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Strategy - Chapter 3: Three Cards */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-black text-blue-400 uppercase tracking-[0.3em] mb-4">{t('lifeAtUHomes.strategyTitle')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-12">
              {t('lifeAtUHomes.strategyText')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Global Expansion */}
            <div className="bg-slate-800/50 backdrop-blur p-8 rounded-2xl border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all group">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('lifeAtUHomes.strategyGlobal')}</h3>
              <p className="text-slate-400 leading-relaxed">{t('lifeAtUHomes.strategyGlobalDesc')}</p>
            </div>

            {/* Digital Foundation */}
            <div className="bg-slate-800/50 backdrop-blur p-8 rounded-2xl border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all group">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-500/30 transition-colors">
                <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('lifeAtUHomes.strategyDigital')}</h3>
              <p className="text-slate-400 leading-relaxed">{t('lifeAtUHomes.strategyDigitalDesc')}</p>
            </div>

            {/* Intelligent Innovation */}
            <div className="bg-slate-800/50 backdrop-blur p-8 rounded-2xl border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all group">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-500/30 transition-colors">
                <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{t('lifeAtUHomes.strategyIntelligent')}</h3>
              <p className="text-slate-400 leading-relaxed">{t('lifeAtUHomes.strategyIntelligentDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Chapter 4: SVG Icons */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4">{t('lifeAtUHomes.valuesTitle')}</h2>
            <p className="text-slate-500 text-lg">{t('lifeAtUHomes.valuesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Integrity */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">{t('lifeAtUHomes.values.integrity')}</h4>
              <p className="text-slate-600 leading-relaxed">{t('lifeAtUHomes.values.integrityDesc')}</p>
            </div>

            {/* Excellence */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">{t('lifeAtUHomes.values.excellence')}</h4>
              <p className="text-slate-600 leading-relaxed">{t('lifeAtUHomes.values.excellenceDesc')}</p>
            </div>

            {/* Long-termism */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-slate-900 mb-3">{t('lifeAtUHomes.values.longTerm')}</h4>
              <p className="text-slate-600 leading-relaxed">{t('lifeAtUHomes.values.longTermDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 精选照片区 */}
      <FeaturedPhotos />

      {/* DEI Section */}
      <section className="py-24 max-w-7xl mx-auto px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">{t('lifeAtUHomes.diversityTitle')}</h2>
          <p className="text-lg md:text-xl text-slate-500 mb-10 leading-relaxed">
            {t('lifeAtUHomes.diversityDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              t('lifeAtUHomes.tags.integrity'),
              t('lifeAtUHomes.tags.excellence'),
              t('lifeAtUHomes.tags.longTerm')
            ].map(tag => (
              <span key={tag} className="px-6 py-2.5 rounded-full bg-slate-100 text-slate-600 font-semibold text-sm cursor-pointer hover:bg-slate-200 transition-colors">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Join Us */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">
            {t('lifeAtUHomes.ctaTitle') || '加入我们的旅程'}
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('lifeAtUHomes.ctaDesc') || '与全球 1200+ 团队成员一起，在 33 个城市重新定义跨境生活体验'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onSearchJobs}
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:bg-slate-50 transition-all cursor-pointer"
            >
              {t('lifeAtUHomes.ctaPrimaryBtn') || '浏览开放职位'}
            </button>
            <button
              onClick={onOffices}
              className="bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all cursor-pointer"
            >
              {t('lifeAtUHomes.ctaSecondaryBtn') || '了解全球办公室'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeAtUHomes;
